import { Show, type Accessor } from 'solid-js';
import type { CpuPoint } from '../state/metrics';
import { HudPanel } from '../ui/hud-panel';
import { SegRing } from '../ui/rings';
import { Sparkline } from '../ui/sparkline';

/** Coarse, human uptime: the dashboard never needs second precision here. */
export function formatUptime(seconds: number): string {
  if (seconds >= 86400) return `up ${Math.floor(seconds / 86400)}d`;
  if (seconds >= 3600) return `up ${Math.floor(seconds / 3600)}h`;
  if (seconds >= 60) return `up ${Math.floor(seconds / 60)}m`;
  return 'up <1m';
}

export function CpuWidget(props: { cpu: Accessor<CpuPoint[]> }) {
  const latest = () => props.cpu().at(-1) ?? null;
  const utilPct = () => {
    const c = latest();
    return c ? Math.round(c.usagePct) : null;
  };

  /** Only facts the agent actually sent — an older agent simply shortens the line. */
  const subtitle = () => {
    const c = latest();
    if (!c) return undefined;
    const parts: string[] = [];
    if (c.threads != null && c.threads > 0) parts.push(`${c.threads} threads`);
    if (c.kernel) parts.push(c.kernel);
    if (c.uptimeS != null) parts.push(formatUptime(c.uptimeS));
    return parts.length ? parts.join(' · ') : undefined;
  };

  return (
    <HudPanel title="CPU" sub={subtitle()}>
      <div class="flex items-center gap-5 max-[500px]:gap-3.5">
        <SegRing value={utilPct()} label="util" segments={latest()?.threads ?? 12} />
        <div class="min-w-0 flex-1">
          <Show
            when={props.cpu().length >= 2}
            fallback={<p class="text-[0.8rem] text-faint">waiting for samples…</p>}
          >
            <Sparkline points={props.cpu().map((p) => p.usagePct)} color="var(--holo)" class="h-[52px]" />
            <div class="hud-mono mt-1.5 text-[0.5938rem] uppercase tracking-[0.14em] text-faint">
              last minute
            </div>
          </Show>
        </div>
      </div>
    </HudPanel>
  );
}
